import { setAnalyticsFarmContext } from 'analytics/ga4';
import { storageKeys } from 'static';
import { AccountInfoData } from 'types';

export type FarmContext = {
  farmId?: string;
  farmName?: string;
  orgNumber?: string;
};

export type FarmOption = {
  farmId: string;
  farmIdCandidates: string[];
  farmName: string;
  label: string;
  orgNumber?: string;
  value: string;
};

export const FARM_CONTEXT_CHANGED_EVENT = 'farm-context-changed';

export const getStringValue = (value: string | number | null | undefined) => {
  if (value === undefined || value === null) {
    return '';
  }

  return String(value).trim();
};

export const getStoredFarmContext = (): FarmContext => {
  if (typeof window === 'undefined') {
    return {};
  }

  return {
    farmId: getStringValue(sessionStorage.getItem(storageKeys.RecentFarmId)) || undefined,
    farmName: getStringValue(sessionStorage.getItem(storageKeys.RecentFarmName)) || undefined,
    orgNumber: getStringValue(sessionStorage.getItem(storageKeys.RecentFarmOrgNumber)) || undefined,
  };
};

const FARM_ID_FIELDS = ['id', 'farmId', 'farmCode'] as const;
type FarmIdField = typeof FARM_ID_FIELDS[number];

const getPreferredFarmIdField = (accountInfo?: AccountInfoData): FarmIdField | undefined => {
  const selectedFarmId = getStringValue(accountInfo?.selectedFarmId);

  if (!selectedFarmId) {
    return undefined;
  }

  return FARM_ID_FIELDS.find((field) =>
    (accountInfo?.userFarms ?? []).some((farm) => getStringValue(farm?.[field]) === selectedFarmId)
  );
};

const getFarmIdCandidates = (
  farm: NonNullable<AccountInfoData['userFarms']>[number],
  preferredFarmIdField?: FarmIdField
) => {
  const fields = preferredFarmIdField
    ? [preferredFarmIdField, ...FARM_ID_FIELDS.filter((field) => field !== preferredFarmIdField)]
    : FARM_ID_FIELDS;

  const candidates: string[] = [];

  fields.forEach((field) => {
    const value = getStringValue(farm?.[field]);

    if (value && !candidates.includes(value)) {
      candidates.push(value);
    }
  });

  return candidates;
};

const getFarmName = (farm: NonNullable<AccountInfoData['userFarms']>[number], fallback: string) =>
  getStringValue(farm?.farmName) ||
  getStringValue(farm?.displayName) ||
  getStringValue(farm?.name) ||
  getStringValue(farm?.companyName) ||
  getStringValue(farm?.farmCompanyName) ||
  fallback;

const getFarmOrgNumber = (farm: NonNullable<AccountInfoData['userFarms']>[number]) =>
  getStringValue(farm?.companyRegistration) ||
  getStringValue(farm?.orgNumber) ||
  getStringValue(farm?.orgNr) ||
  getStringValue(farm?.organizationNumber) ||
  getStringValue(farm?.organisationNumber);

export const getUserFarmOptions = (accountInfo?: AccountInfoData): FarmOption[] => {
  const preferredFarmIdField = getPreferredFarmIdField(accountInfo);

  const farmOptions = (accountInfo?.userFarms ?? []).reduce<FarmOption[]>((options, farm, index) => {
    const farmIdCandidates = getFarmIdCandidates(farm, preferredFarmIdField);
    const farmId = getStringValue(farm?.id) || farmIdCandidates[0];
    const label = getFarmName(farm, farmId);

    if (!farmId) {
      return options;
    }

    const sameFarm = options.some((option) =>
      option.farmId === farmId
      || option.farmIdCandidates.some((candidate) => farmIdCandidates.includes(candidate))
      || option.label.localeCompare(label, 'sv', { sensitivity: 'base' }) === 0
    );
    if (sameFarm) {
      return options;
    }

    options.push({
      farmId,
      farmIdCandidates,
      farmName: label,
      label,
      orgNumber: getFarmOrgNumber(farm) || undefined,
      value: `${farmId}-${index}`,
    });

    return options;
  }, []);

  return farmOptions.sort((left, right) => left.label.localeCompare(right.label, 'sv', { sensitivity: 'base' }));
};

/** User has at least one real (non-demo) farm — required before opening product apps. */
export const hasRegisteredFarm = (accountInfo?: AccountInfoData) =>
  getUserFarmOptions(accountInfo).length > 0;

export const needsFarmRegistration = (accountInfo?: AccountInfoData) =>
  !hasRegisteredFarm(accountInfo);

export const findFarmOptionById = (farmOptions: FarmOption[], farmId?: string | number | null) => {
  const normalizedFarmId = getStringValue(farmId);

  if (!normalizedFarmId) {
    return undefined;
  }

  return farmOptions.find((option) => option.farmIdCandidates.includes(normalizedFarmId));
};

export const userHasFarmAccess = (accountInfo: AccountInfoData | undefined, farmId?: string | number | null) =>
  findFarmOptionById(getUserFarmOptions(accountInfo), farmId) !== undefined;

export const getFarmContextFromAccountInfo = (
  accountInfo?: AccountInfoData,
  preferredFarmId?: string | number | null
): FarmContext => {
  const storedFarmContext = getStoredFarmContext();
  const farmOptions = getUserFarmOptions(accountInfo);
  const preferredId = getStringValue(preferredFarmId) || storedFarmContext.farmId || '';

  if (preferredId) {
    const matchingOption = findFarmOptionById(farmOptions, preferredId);

    if (matchingOption) {
      return {
        farmId: matchingOption.farmId,
        farmName: matchingOption.farmName,
        orgNumber: matchingOption.orgNumber,
      };
    }

    if (!farmOptions.length) {
      return {};
    }
  }

  const selectedFarmId = getStringValue(accountInfo?.selectedFarmId);
  const selectedFarmName = getStringValue(accountInfo?.selectedFarmName);

  if (selectedFarmId) {
    const matchingOption = findFarmOptionById(farmOptions, selectedFarmId);

    if (matchingOption) {
      return {
        farmId: matchingOption.farmId,
        farmName: matchingOption.farmName,
        orgNumber: matchingOption.orgNumber,
      };
    }

    if (farmOptions.length) {
      const firstOption = farmOptions[0];

      return {
        farmId: firstOption.farmId,
        farmName: firstOption.farmName,
        orgNumber: firstOption.orgNumber,
      };
    }

    return {
      farmId: selectedFarmId,
      farmName: selectedFarmName || undefined,
      orgNumber: selectedFarmId === storedFarmContext.farmId ? storedFarmContext.orgNumber : undefined,
    };
  }

  if (selectedFarmName) {
    const matchingOption = farmOptions.find((option) => option.farmName.toLowerCase() === selectedFarmName.toLowerCase());

    if (matchingOption) {
      return {
        farmId: matchingOption.farmId,
        farmName: matchingOption.farmName,
        orgNumber: matchingOption.orgNumber,
      };
    }

    if (farmOptions.length) {
      const firstOption = farmOptions[0];

      return {
        farmId: firstOption.farmId,
        farmName: firstOption.farmName,
        orgNumber: firstOption.orgNumber,
      };
    }

    return {
      farmName: selectedFarmName,
      orgNumber: storedFarmContext.orgNumber,
    };
  }

  const firstOption = farmOptions[0];

  if (firstOption) {
    return {
      farmId: firstOption.farmId,
      farmName: firstOption.farmName,
      orgNumber: firstOption.orgNumber,
    };
  }

  return {};
};

export const dispatchFarmContextChanged = (farmContext: FarmContext): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent<FarmContext>(FARM_CONTEXT_CHANGED_EVENT, { detail: farmContext }));
};

export const setCurrentFarmContext = (farmContext: FarmContext): void => {
  setAnalyticsFarmContext(farmContext.farmId, farmContext.farmName, farmContext.orgNumber);
  dispatchFarmContextChanged(farmContext);
};
